import React, { useState, useEffect, useRef } from 'react';
import { useLandingPageData } from '../../context/LandingPageContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../i18n/translations';
import { ArrowLeftRight, MessageCircle, Lock, Info } from 'lucide-react';
import { Button } from '../ui/button';

const BeforeAfterCard = ({ item, onAnimationComplete, isDark, analysisType }) => {
  const { language } = useLanguage();
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [animationPhase, setAnimationPhase] = useState('idle');
  const cardRef = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated.current) {
            hasAnimated.current = true;
            startAnimation();
          }
        });
      },
      { threshold: 0.5 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const startAnimation = () => {
    setAnimationPhase('animating');
    setSliderPosition(0);
    
    setTimeout(() => setSliderPosition(100), 800);
    setTimeout(() => setSliderPosition(50), 1600);
    setTimeout(() => {
      setAnimationPhase('complete');
      setShowOverlay(true);
      if (onAnimationComplete) onAnimationComplete();
    }, 8000);
  };

  const handleMove = (clientX, rect) => {
    if (showOverlay) return;
    const x = clientX - rect.left;
    const percentage = Math.min(Math.max((x / rect.width) * 100, 0), 100);
    setSliderPosition(percentage);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || showOverlay) return;
    const rect = e.currentTarget.getBoundingClientRect();
    handleMove(e.clientX, rect);
  };

  const handleTouchMove = (e) => {
    if (showOverlay) return;
    const rect = e.currentTarget.getBoundingClientRect();
    handleMove(e.touches[0].clientX, rect);
  };

  const openWhatsApp = () => {
    const textMap = {
      hair: {
        de: 'Hallo, ich interessiere mich für Vorher-Nachher Bilder Ihrer Haartransplantationen.',
        en: 'Hello, I am interested in before-after images of your hair transplants.',
      },
      beard: {
        de: 'Hallo, ich interessiere mich für Vorher-Nachher Bilder Ihrer Barthaartransplantationen.',
        en: 'Hello, I am interested in before-after images of your beard transplants.',
      },
      eyebrow: {
        de: 'Hallo, ich interessiere mich für Vorher-Nachher Bilder Ihrer Augenbrauentransplantationen.',
        en: 'Hello, I am interested in before-after images of your eyebrow transplants.',
      },
    };
    const msg = textMap[analysisType]?.[language] || textMap.hair[language] || textMap.hair.de;
    const whatsappUrl = `https://wa.me/491722888879?text=${encodeURIComponent(msg)}`;
    if (typeof window.gtag_report_whatsapp === 'function') {
      window.gtag_report_whatsapp(whatsappUrl);
    } else {
      window.open(whatsappUrl, '_blank');
    }
  };

  return (
    <div 
      ref={cardRef}
      className={`group relative rounded-2xl overflow-hidden transition-all duration-500 ${
        isDark 
          ? 'bg-[#1a1a1a] border border-white/5 hover:border-[#7BA7C2]/30' 
          : 'bg-white border border-gray-200 hover:border-[#5A8BA6]/30'
      }`}
    >
      <div
        className="relative aspect-square cursor-ew-resize select-none overflow-hidden"
        onMouseDown={() => !showOverlay && setIsDragging(true)}
        onMouseUp={() => setIsDragging(false)}
        onMouseLeave={() => setIsDragging(false)}
        onMouseMove={handleMouseMove}
        onTouchMove={handleTouchMove}
      >
        {/* After Image */}
        <img 
          src={item.after} 
          alt={language === 'de' ? `Haartransplantation Nachher Ergebnis ${item.grafts} - ${item.title} Dion Hair Clinic` : `Hair Transplant After Result ${item.grafts} - ${item.title} Dion Hair Clinic`} 
          decoding="async"
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: item.objectPosition || 'center' }}
        />
        <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-xs font-medium z-10">
          {language === 'de' ? 'Nachher' : 'After'}
        </div>

        {/* Before Image with clip-path */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ 
            clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`,
            transition: animationPhase === 'animating' ? 'clip-path 0.8s ease-in-out' : 'none'
          }}
        >
          <img 
            src={item.before} 
            alt={language === 'de' ? `Haartransplantation Vorher ${item.technique} - ${item.title} Dion Hair Clinic` : `Hair Transplant Before ${item.technique} - ${item.title} Dion Hair Clinic`} 
            decoding="async"
            loading="lazy"
            className="w-full h-full object-cover"
            style={{ objectPosition: item.objectPosition || 'center' }}
          />
          <div className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-medium ${
            isDark ? 'bg-[#7BA7C2]/20 text-[#7BA7C2]' : 'bg-[#5A8BA6]/20 text-[#5A8BA6]'
          }`}>
            {language === 'de' ? 'Vorher' : 'Before'}
          </div>
        </div>

        {/* Slider Line */}
        <div
          className={`absolute top-0 bottom-0 w-1 shadow-lg ${isDark ? 'bg-white shadow-black/50' : 'bg-[#5A8BA6] shadow-[#5A8BA6]/30'}`}
          style={{ 
            left: `${sliderPosition}%`, 
            transform: 'translateX(-50%)',
            transition: animationPhase === 'animating' ? 'left 0.8s ease-in-out' : 'none'
          }}
        >
          <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full shadow-xl flex items-center justify-center ${
            isDark ? 'bg-white' : 'bg-[#5A8BA6]'
          }`}>
            <ArrowLeftRight className={`w-5 h-5 ${isDark ? 'text-gray-800' : 'text-white'}`} />
          </div>
        </div>

        {/* Overlay */}
        {showOverlay && (
          <div className="absolute inset-0 flex items-center justify-center animate-fade-in">
            <div className={`absolute inset-0 ${
              isDark 
                ? 'bg-[#0a0a0a]/95' 
                : 'bg-white/95'
            }`} />
            
            <div className="relative z-10 text-center px-6 py-8 max-w-[90%]">
              <div className={`inline-flex items-center justify-center w-14 h-14 rounded-full border mb-5 ${
                isDark ? 'bg-[#7BA7C2]/10 border-[#7BA7C2]/20' : 'bg-[#5A8BA6]/10 border-[#5A8BA6]/20'
              }`}>
                <Lock className={`w-6 h-6 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
              </div>
              
              <h4 className={`font-semibold text-lg mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {language === 'de' ? 'Vorher-Nachher Bilder' : 'Before-After Images'}
              </h4>
              <p className={`text-sm leading-relaxed mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {language === 'de' 
                  ? 'Aufgrund des Heilmittelwerbegesetzes (HWG) dürfen wir Vorher-Nachher Bilder nicht öffentlich zeigen.'
                  : 'Due to German advertising regulations, we cannot publicly display before-after images.'}
              </p>
              
              <button
                onClick={openWhatsApp}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#25D366] hover:bg-[#20BD5A] text-white font-medium text-sm transition-all duration-300 hover:scale-105 shadow-lg shadow-[#25D366]/25"
              >
                <MessageCircle className="w-4 h-4" />
                {language === 'de' ? 'Bilder anfordern' : 'Request Images'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const BeforeAfter = ({ onOpenConsultation }) => {
  const { beforeAfterImages, features } = useLandingPageData();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const analysisType = features?.analysisType || 'hair';
  
  return (
    <section id="vorher-nachher" className={`py-24 relative overflow-hidden transition-colors duration-300 ${
      isDark ? 'bg-[#0f0f0f]' : 'bg-[#f8f9fa]'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium mb-4 ${
            isDark ? 'bg-[#7BA7C2]/10 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 text-[#5A8BA6]'
          }`}>
            {t('beforeAfter.sectionBadge')}
          </span>
          <h2 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('beforeAfter.before')} & <span className="bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] bg-clip-text text-transparent">{t('beforeAfter.after')}</span>
          </h2>
          <p className={`max-w-2xl mx-auto text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {t('beforeAfter.sectionDescription')}
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {beforeAfterImages.map((item) => (
            <BeforeAfterCard key={item.id} item={item} isDark={isDark} analysisType={analysisType} />
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {language === 'de' ? 'Möchten Sie ähnliche Ergebnisse erzielen?' : 'Would you like to achieve similar results?'}
          </p>
          <Button
            onClick={onOpenConsultation}
            className="bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] hover:from-[#5A8BA6] hover:to-[#7BA7C2] text-white px-8 py-6 text-lg shadow-lg shadow-[#7BA7C2]/25"
          >
            {language === 'de' ? 'Kostenlose Analyse vereinbaren' : 'Schedule Free Analysis'}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default BeforeAfter;
